import { DEFAULTS } from "@/react/story/data";
import { IriEnums } from "@/react/story/enums/IriEnums";
import { useConfig } from "@/react/story/providers/ConfigContext";
import { StoryContext } from "@/react/story/providers/StoryContext";
import type { Story } from "@/react/story/types";
import ApiUtils from "@/react/story/utils/ApiUtils";
import InitUtils from "@/react/story/utils/InitUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type CSSProperties, type ReactNode, useMemo } from "react";

type Props = {
    defaultStory: Story;
    children: ReactNode;
}

export default function StoryProvider(props: Props) {
    const Config = useConfig();
    const QueryClient = useQueryClient();

    const QueryStory = useQuery({
        queryKey: [ "story" ],
        queryFn: () => ApiUtils.get<Story>(props.defaultStory["@id"]),
        initialData: props.defaultStory,
        initialDataUpdatedAt: new Date().getTime(),
        refetchOnWindowFocus: false,
        enabled: !Config.isOnline,
    });

    const MutationStorySave = useMutation({
        mutationFn: (story: Story) => ApiUtils.save<Story>(IriEnums.STORY, story),
        onSuccess: (story: Story) => Promise.all([
            QueryClient.setQueryData([ "story" ], story),
        ]),
    });

    const patch = async (key: string, value: unknown): Promise<void> => {
        // Copie profonde : `setDeepValue` écrit dans les objets imbriqués, et une copie de
        // surface les partage avec la story rangée dans le cache de TanStack Query — un
        // `patch("config.width", …)` la modifierait donc avant même la réponse du serveur.
        const s = structuredClone(QueryStory.data);
        InitUtils.setDeepValue(s, key, value);
        MutationStorySave.mutate(s);
    };

    const context = useMemo(() => ({
        story: QueryStory?.data,
        patch,
        getViewerStyle: (): CSSProperties => {
            let w = QueryStory.data.config.width ?? DEFAULTS.STORY.width;
            let h = QueryStory.data.config.height ?? DEFAULTS.STORY.height;

            if (Config.isOnline) {
                return {
                    width: `${w}px`,
                    flexBasis: `${w}px`,
                    height: `${h}px`,
                };
            }

            // respect ratio
            const ratio = w / h;
            w = Math.min(w, 400);
            h = Math.round(w / ratio);

            return {
                width: `${w}px`,
                flexBasis: `${w}px`,
                height: `${h}px`,
            };
        }
    }), [ QueryStory?.data, Config.isOnline ]);

    return (
        <StoryContext.Provider value={context}>
            {props.children}
        </StoryContext.Provider>
    );
}
